import { Button, Group } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconUsers, IconX } from '@tabler/icons-react';
import { useEffect, useRef } from 'react';

import { useSoundPlayer } from '../reactions';
import { type Lobby, type LobbyRequest, useLobby } from './lobby';
import { i18n } from '@/i18n';

const QUEUE_NOTIFICATION_ID = 'lobby-queue';
const CHIME_INTERVAL_MS = 15_000;

export function LobbyNotifications({ onShowQueue }: Readonly<{ onShowQueue: () => void }>) {
    const lobby = useLobby();
    return lobby && <QueueNotifications lobby={lobby} onShowQueue={onShowQueue} />;
}

function QueueNotifications({ lobby, onShowQueue }: Readonly<{ lobby: Lobby; onShowQueue: () => void }>) {
    const { requests, admit, reject, admitAll } = lobby;
    const shownRef = useRef(new Set<string>());
    const queueShownRef = useRef(false);
    useLobbyChime(requests);

    useEffect(() => {
        const shown = shownRef.current;
        const single = requests.length === 1 ? requests[0] : undefined;
        for (const guestId of shown) {
            if (guestId === single?.guestId) continue;
            shown.delete(guestId);
            notifications.hide(`lobby-${guestId}`);
        }
        if (single && !shown.has(single.guestId)) {
            shown.add(single.guestId);
            showRequest(single, admit, reject);
        }

        if (requests.length < 2) {
            if (queueShownRef.current) notifications.hide(QUEUE_NOTIFICATION_ID);
            queueShownRef.current = false;
            return;
        }
        const queue = {
            id: QUEUE_NOTIFICATION_ID,
            autoClose: false,
            allowClose: false,
            title: i18n.t('conference:lobby.queueTitle', { count: requests.length }),
            message: (
                <Group gap="var(--spacing-2)" mt="var(--spacing-2)">
                    <Button
                        leftSection={<IconCheck size={16} />}
                        size="sm"
                        variant="filled"
                        onClick={() => {
                            notifications.hide(QUEUE_NOTIFICATION_ID);
                            void admitAll();
                        }}
                    >
                        {i18n.t('conference:lobby.admitAll')}
                    </Button>
                    <Button leftSection={<IconUsers size={16} />} size="sm" variant="default" onClick={onShowQueue}>
                        {i18n.t('conference:lobby.showQueue')}
                    </Button>
                </Group>
            ),
            onClose: () => {
                queueShownRef.current = false;
            },
        };
        if (queueShownRef.current) {
            notifications.update(queue);
        } else {
            queueShownRef.current = true;
            notifications.show(queue);
        }
    }, [requests, admit, reject, admitAll, onShowQueue]);

    useEffect(() => {
        const shown = shownRef.current;
        return () => {
            for (const guestId of shown) notifications.hide(`lobby-${guestId}`);
            shown.clear();
            notifications.hide(QUEUE_NOTIFICATION_ID);
        };
    }, []);

    return null;
}

function showRequest({ guestId, name }: LobbyRequest, admit: Lobby['admit'], reject: Lobby['reject']) {
    const id = `lobby-${guestId}`;
    notifications.show({
        id,
        autoClose: false,
        allowClose: false,
        title: i18n.t('conference:lobby.requestTitle', { name }),
        message: (
            <Group gap="var(--spacing-2)" mt="var(--spacing-2)">
                <Button
                    leftSection={<IconCheck size={16} />}
                    size="sm"
                    variant="filled"
                    onClick={() => {
                        notifications.hide(id);
                        void admit(guestId);
                    }}
                >
                    {i18n.t('conference:lobby.admit')}
                </Button>
                <Button
                    leftSection={<IconX size={16} />}
                    size="sm"
                    variant="default"
                    onClick={() => {
                        notifications.hide(id);
                        void reject(guestId);
                    }}
                >
                    {i18n.t('conference:lobby.reject')}
                </Button>
            </Group>
        ),
    });
}

function useLobbyChime(requests: LobbyRequest[]) {
    const playSound = useSoundPlayer();
    const queueKey = requests.map(({ guestId }) => guestId).join('|');
    const knownRef = useRef<ReadonlySet<string>>(new Set());
    const lastChimeAtRef = useRef(0);

    useEffect(() => {
        const waiting = queueKey ? queueKey.split('|') : [];
        const known = knownRef.current;
        knownRef.current = new Set(waiting);
        const chime = () => {
            lastChimeAtRef.current = Date.now();
            playSound('lobby-request');
        };
        const arrived = waiting.some((guestId) => !known.has(guestId));
        if (arrived && (known.size === 0 || Date.now() - lastChimeAtRef.current >= CHIME_INTERVAL_MS)) chime();
        if (waiting.length === 0) return;
        const reminder = globalThis.window.setInterval(chime, CHIME_INTERVAL_MS);
        return () => {
            globalThis.window.clearInterval(reminder);
        };
    }, [queueKey, playSound]);
}
